import React from "react";

import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { abs } from "~/tools/abs";
import { differenceInMilliseconds, intervalToDuration, millisecondsToMinutes } from "~/tools/date-time";
import { formatAbsoluteTime } from "~/tools/date-time/format/format-absolute-time";
import type { Time } from "~/tools/date-time/time";
import { isPresent } from "~/tools/is-present";
import { useNow } from "~/tools/use-now";

export interface AgeProps {
	start: Time;
	end: undefined | null | Time;
}

export const Age: React.FC<AgeProps> = ({ start, end }) => {
	const text = useNow({
		pause: end != null,
		transform: (now) => {
			const duration = differenceInMilliseconds(end ?? now, start);
			const { years, months, weeks, days, hours, minutes, seconds } = intervalToDuration({
				start,
				end: end ?? now,
			});

			return [
				{ value: years, unit: "y" },
				{ value: months, unit: "M" },
				{ value: weeks, unit: "w" },
				{ value: days, unit: "d" },
				{ value: hours, unit: "h" },
				{ value: minutes, unit: "m" },
				// hide seconds if the duration is longer than 15 minutes
				{ value: abs(millisecondsToMinutes(duration)) < 15 ? seconds : 0, unit: "s" },
			]
				.map(({ value, unit }) => (value ? `${value}${unit}` : null))
				.filter(isPresent)
				.slice(0, 2) // Only take the 2 most significant numbers
				.join(" ");
		},
	});

	return (
		<Tooltip>
			<TooltipTrigger>
				<span>{text}</span>
			</TooltipTrigger>

			<TooltipContent>{formatAbsoluteTime(start).toString()}</TooltipContent>
		</Tooltip>
	);
};
